import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AdvancedFilters, {
  EMPTY_FILTERS,
  FilterOptions,
  GlobalFilters,
} from '../components/admin/AdvancedFilters';
import AlertsDrawer, { CreateAlertData } from '../components/admin/AlertsDrawer';
import ApplicationListTable from '../components/admin/ApplicationListTable';
import EvaluationsModal from '../components/admin/EvaluationsModal';
import EvaluatorAssignmentModal from '../components/admin/EvaluatorAssignmentModal';
import MultiSelectSearch from '../components/admin/MultiSelectSearch';
import NotifyResultsModal, {
  NotificationResult,
  NotificationStats,
} from '../components/admin/NotifyResultsModal';
import Button from 'shared/components/UC/Button';
import Toast from 'shared/components/Toast';
import { useUser } from '../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import adminService from '../services/adminService';
import applicationService from '../services/applicationService';
import {
  AlertFilters,
  AlertStatus,
  AlertType,
  ApplicationAlert,
  ApplicationWithAdminData,
  PaginationData,
  StatusConfig,
} from '../types/admin';
import { EvaluatorAssignment } from '../types/evaluator';
import {
  canAccessAlerts,
  canDownloadEvaluations,
  canUserPerformAction,
  getVisibleStatusesForUser,
} from '../utils/permissionUtils';
import {
  getRevertConfirmationMessage,
  getRevertErrorMessage,
  getRevertTargetStatus,
} from '../utils/revertUtils';
import BulkAssignmentTab from '../components/admin/bulk/BulkAssignmentTab';
import EvaluadoresTab from '../components/admin/EvaluadoresTab';
import EvaluationsDashboard from './EvaluationsDashboard';
import styles from './AdminDashboard.module.css';

const AdminDashboard: React.FC = () => {
  const user = useUser();
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [contestName, setContestName] = useState<string>('');
  const [contestDeadline, setContestDeadline] = useState<string | null>(null);
  const [contestIsActive, setContestIsActive] = useState<boolean>(true);

  // Define which statuses to load based on user permissions
  const statusesToLoad = getVisibleStatusesForUser(user);

  // Initialize pagination state for all statuses
  const initializePagination = () => {
    const initialPagination: Record<string, PaginationData> = {};
    statusesToLoad.forEach((status) => {
      initialPagination[status] = {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1,
      };
    });
    return initialPagination;
  };

  const [applications, setApplications] = useState<ApplicationWithAdminData[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newApplicationsMessage, setNewApplicationsMessage] = useState<string | null>(null);
  const [statusPagination, setStatusPagination] = useState<
    Record<string, PaginationData>
  >(initializePagination());
  const [, setOriginalTotals] = useState<Record<string, number>>(
    {}
  );

  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'error' | 'success' | 'warning' | 'info';
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [globalFilters, setGlobalFilters] = useState<GlobalFilters>(EMPTY_FILTERS);

  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    unidadAcademica: [],
    evaluators: [],
  });

  // Alerts state
  const [alerts, setAlerts] = useState<ApplicationAlert[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsPagination, setAlertsPagination] = useState<PaginationData>({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [alertsFilters, setAlertsFilters] = useState<AlertFilters>({
    status: AlertStatus.OPEN,
  });
  const [alertsSearchTerm, setAlertsSearchTerm] = useState('');
  const [alertsDrawerOpen, setAlertsDrawerOpen] = useState(false);
  const [alertStats, setAlertStats] = useState<{
    openByType: Partial<Record<AlertType, number>>;
    totalOpen: number;
    totalResolved: number;
    totalDismissed: number;
  }>({ openByType: {}, totalOpen: 0, totalResolved: 0, totalDismissed: 0 });
  // Evaluator modal state
  const [evaluatorModal, setEvaluatorModal] = useState<{
    isOpen: boolean;
    application: ApplicationWithAdminData | null;
  }>({
    isOpen: false,
    application: null,
  });

  // Evaluations modal state
  const [evaluationsModal, setEvaluationsModal] = useState<{
    isOpen: boolean;
    application: ApplicationWithAdminData | null;
  }>({
    isOpen: false,
    application: null,
  });

  // Notification modal state
  const [notifyModal, setNotifyModal] = useState<{
    isOpen: boolean;
    stats: NotificationStats | null;
  }>({
    isOpen: false,
    stats: null,
  });

  // Supplementary stats
  const [, setEvaluatorStats] = useState({
    total: 0,
    completed: 0,
    inProgress: 0,
    notStarted: 0,
    conflictDeclared: 0,
    declined: 0,
    noConflict: 0,
    pendingDeclaration: 0,
  });
  const [allUAs, setAllUAs] = useState<Array<{ name: string; count: number }>>([]);
  const [showUAModal, setShowUAModal] = useState(false);
  type UAFacultyRow = { faculty: string; total: number; units: Array<{ name: string; count: number }> };
  const [uaByFaculty, setUaByFaculty] = useState<UAFacultyRow[]>([]);
  const [uaView, setUaView] = useState<'chart' | 'table'>('chart');
  const [instView, setInstView] = useState<'chart' | 'table'>('chart');
  const [oecdView, setOecdView] = useState<'chart' | 'table'>('chart');
  const [teamCompView, setTeamCompView] = useState<'chart' | 'table'>('chart');

  // Section tabs
  const [activeTab, setActiveTab] = useState<
    'postulaciones' | 'asignacion-masiva' | 'evaluaciones' | 'evaluadores' | 'reportes'
  >('postulaciones');
  const [showEstadoModal, setShowEstadoModal] = useState(false);
  // Cuando el asistente de importación deja asignaciones sin invitar, abre
  // Evaluaciones directamente en la sub-vista por evaluador y con el filtro
  // puesto. Entrar al tab a mano lo deja como siempre.
  const [focusPendingInvitations, setFocusPendingInvitations] = useState(false);

  // Drawer "Estado del concurso": cerrar con Escape + bloquear el scroll del body
  // mientras está abierto (mismo comportamiento que el drawer de alertas).
  useEffect(() => {
    if (!showEstadoModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowEstadoModal(false);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [showEstadoModal]);

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportStatuses, setExportStatuses] = useState<string[]>([]);
  const [exportAcademicUnits, setExportAcademicUnits] = useState<string[]>([]);

  // Reports state (lazy-loaded when reportes tab is activated)
  const [reportsLoaded, setReportsLoaded] = useState(false);
  const [, setRep010] = useState<Awaited<ReturnType<typeof adminService.getEvaluatorAssignmentReport>>>([]);
  const [, setRep011] = useState<Awaited<ReturnType<typeof adminService.getRejectedAssignments>>>([]);
  const [, setRep012] = useState<Awaited<ReturnType<typeof adminService.getOverdueEvaluations>>>([]);
  const [, setRep013] = useState<Awaited<ReturnType<typeof adminService.getInactiveEvaluatorReport>>>([]);
  const [, setPoolStats] = useState<Awaited<ReturnType<typeof adminService.getAssignmentPoolStats>>>({ total: 0, assigned: 0, unassigned: 0 });
  // Evaluadores tab state (lazy-loaded)
  const [evaluadoresLoaded, setEvaluadoresLoaded] = useState(false);
  const [, setEvalDashboard] = useState<Awaited<ReturnType<typeof adminService.getEvaluatorDashboard>>>([]);
  // Tableros REP-001
  const [oecdDist, setOecdDist] = useState<Awaited<ReturnType<typeof adminService.getOecdDistribution>>>([]);
  const [admFunnel, setAdmFunnel] = useState<Awaited<ReturnType<typeof adminService.getAdmissibilityFunnel>>>({ drafts: 0, submitted: 0, pending: 0, underReview: 0, admissible: 0, notAdmissible: 0 });
  const [evalFunnel, setEvalFunnel] = useState<Awaited<ReturnType<typeof adminService.getEvaluationFunnel>>>({ admissible: 0, pendingEval: 0, underEvaluation: 0, evaluated: 0, awarded: 0, notAwarded: 0 });
  const [, setArbitrationApps] = useState<Awaited<ReturnType<typeof adminService.getArbitrationApplications>>>([]);
  const [instReport, setInstReport] = useState<Awaited<ReturnType<typeof adminService.getInstitutionReport>>>({ totalUnique: 0, national: 0, international: 0, withInternationalCollab: 0, byCountry: [] });
  const [compReport, setCompReport] = useState<Awaited<ReturnType<typeof adminService.getProjectCompositionReport>>>({ total: 0, consolidated: 0, emerging: 0, totalPeople: 0, irCount: 0, ipCount: 0, iaCount: 0, coiCount: 0, menTotal: 0, womenTotal: 0, irMen: 0, irWomen: 0, irConsolidated: 0, irEmerging: 0, ipConsolidated: 0, ipEmerging: 0, uniquePeople: 0, uniqueMen: 0, uniqueWomen: 0, ucCombinations: [], coiNone: 0, coiNationalOnly: 0, coiInternationalOnly: 0, coiBoth: 0 });
  const [copiedFunnelLine, setCopiedFunnelLine] = useState<'ingreso' | 'adm' | 'eval' | 'fallo' | 'inst' | null>(null);
  const [budgetTotals, setBudgetTotals] = useState<Awaited<ReturnType<typeof adminService.getBudgetTotals>>>({
    drafts: { montoSolicitado: 0, montoContrapartes: 0, count: 0 },
    submitted: { montoSolicitado: 0, montoContrapartes: 0, count: 0 },
  });
  // Sort state for each report table

  const loadSupplementaryStats = async () => {
    try {
      const [evalStats, uas, dash, uaFac] = await Promise.all([
        adminService.getEvaluatorStats(templateId),
        adminService.getTopUAs(templateId),
        adminService.getEvaluatorDashboard(templateId),
        adminService.getUAsByFaculty(templateId),
      ]);
      setEvaluatorStats(evalStats);
      setAllUAs(uas);
      setEvalDashboard(dash);
      setUaByFaculty(uaFac);
      setEvaluadoresLoaded(true);
    } catch {
      // non-critical
    }
  };

  // Load filter options on mount
  const loadFilterOptions = async () => {
    try {
      const options = await adminService.getFilterOptions(templateId);
      setFilterOptions({
        unidadAcademica: options.unidadAcademica,
        evaluators: options.evaluators,
      });
    } catch (err) {
      console.error('Error loading filter options:', err);
    }
  };

  const loadApplications = async () => {
    try {
      setLoading(true);
      setError(null);

      // For non-superadmins, fetch admissible, under_evaluation, and evaluated together
      const isSuperAdminUser =
        user?.role === 'admin' && user?.userProfile?.affiliation === 'TODAS';

      // Load applications for each status in parallel with filters including search
      const statusRequests = statusesToLoad.map((status) => {
        // For non-superadmins viewing 'admissible', fetch multiple statuses in one call
        if (!isSuperAdminUser && status === 'admissible') {
          return adminService.getApplicationsByStatus(
            ['admissible', 'under_evaluation', 'evaluated'],
            1,
            10,
            { search: searchTerm },
            templateId
          );
        }

        return adminService.getApplicationsByStatus(
          status, 1, 10,
          { search: searchTerm },
          templateId
        );
      });

      const results = await Promise.all(statusRequests);

      // Combine all applications
      const allApplications: ApplicationWithAdminData[] = [];
      const newStatusPagination: Record<string, PaginationData> = {};

      results.forEach((result, index) => {
        const status = statusesToLoad[index];
        allApplications.push(...result.applications);
        newStatusPagination[status] = result.pagination;
      });

      setApplications(allApplications);
      setStatusPagination(newStatusPagination);

      // Store original totals for global stats (unfiltered)
      const newOriginalTotals: Record<string, number> = {};
      results.forEach((result, index) => {
        const status = statusesToLoad[index];
        newOriginalTotals[status] = result.pagination.total;
      });
      setOriginalTotals(newOriginalTotals);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Error al cargar aplicaciones'
      );
    } finally {
      setLoading(false);
    }
  };

  const loadApplicationsForStatus = async (status: string, page: number) => {
    try {
      const isSuperAdminUser =
        user?.role === 'admin' && user?.userProfile?.affiliation === 'TODAS';

      // Get current page size for this status, default to 10
      const currentPageSize = statusPagination[status]?.limit || 10;

      let result: {
        applications: ApplicationWithAdminData[];
        pagination: PaginationData;
      };
      let statusesToRemove: string[] = [status];

      // For non-superadmins viewing 'admissible', fetch multiple statuses in one call
      if (!isSuperAdminUser && status === 'admissible') {
        result = await adminService.getApplicationsByStatus(
          ['admissible', 'under_evaluation', 'evaluated'],
          page,
          currentPageSize,
          { search: searchTerm },
          templateId
        );
        statusesToRemove = ['admissible', 'under_evaluation', 'evaluated'];
      } else {
        result = await adminService.getApplicationsByStatus(
          status, page, currentPageSize,
          { search: searchTerm },
          templateId
        );
      }

      // Update applications for this status
      setApplications((prevApps) => {
        // Remove existing applications for these statuses
        const filteredApps = prevApps.filter(
          (app) => !statusesToRemove.includes(app.status)
        );
        // Add new applications for this status
        return [...filteredApps, ...result.applications];
      });

      // Update pagination for this status
      setStatusPagination((prev) => ({
        ...prev,
        [status]: result.pagination,
      }));
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Error al cargar aplicaciones de estado ${status}`
      );
    }
  };


  const handleStatusPageChange = (status: string, newPage: number) => {
    loadApplicationsForStatus(status, newPage);
  };

  const handleStatusPageSizeChange = async (status: string, newPageSize: number) => {
    try {
      // Save scroll position
      const scrollPosition = window.scrollY;

      const isSuperAdminUser =
        user?.role === 'admin' && user?.userProfile?.affiliation === 'TODAS';

      let result: {
        applications: ApplicationWithAdminData[];
        pagination: PaginationData;
      };
      let statusesToRemove: string[] = [status];

      // For non-superadmins viewing 'admissible', fetch multiple statuses in one call
      if (!isSuperAdminUser && status === 'admissible') {
        result = await adminService.getApplicationsByStatus(
          ['admissible', 'under_evaluation', 'evaluated'],
          1,
          newPageSize,
          { search: searchTerm },
          templateId
        );
        statusesToRemove = ['admissible', 'under_evaluation', 'evaluated'];
      } else {
        result = await adminService.getApplicationsByStatus(
          status,
          1,
          newPageSize,
          { search: searchTerm },
          templateId
        );
      }

      // Update applications for this status
      setApplications((prevApps) => {
        // Remove existing applications for these statuses
        const filteredApps = prevApps.filter(
          (app) => !statusesToRemove.includes(app.status)
        );
        // Add new applications for this status
        return [...filteredApps, ...result.applications];
      });

      // Update pagination for this status with new page size
      setStatusPagination((prev) => ({
        ...prev,
        [status]: result.pagination,
      }));

      // Restore scroll position
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 0);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Error al cambiar tamaño de página para estado ${status}`
      );
    }
  };

  const handleRefreshApplications = async () => {
    try {
      setRefreshing(true);
      // Capture the count before refresh
      const prevTotalCount = applications.length;

      // Manually load all statuses to capture the new data
      const isSuperAdminUser =
        user?.role === 'admin' && user?.userProfile?.affiliation === 'TODAS';

      const statusRequests = statusesToLoad.map((status) => {
        if (!isSuperAdminUser && status === 'admissible') {
          return adminService.getApplicationsByStatus(
            ['admissible', 'under_evaluation', 'evaluated'],
            1,
            10,
            { search: searchTerm },
            templateId
          );
        }
        return adminService.getApplicationsByStatus(
          status, 1, 10,
          { search: searchTerm },
          templateId
        );
      });

      const results = await Promise.all(statusRequests);

      // Combine all applications
      const allApplications: ApplicationWithAdminData[] = [];
      const newStatusPagination: Record<string, PaginationData> = {};

      results.forEach((result, index) => {
        const status = statusesToLoad[index];
        allApplications.push(...result.applications);
        newStatusPagination[status] = result.pagination;
      });

      // Compare the counts
      const newTotalCount = allApplications.length;
      setApplications(allApplications);
      setStatusPagination(newStatusPagination);

      // Show feedback based on comparison
      if (newTotalCount === prevTotalCount) {
        setNewApplicationsMessage('No se han recibido nuevas postulaciones');
      } else {
        setNewApplicationsMessage('Se han recibido nuevas postulaciones');
      }

      setTimeout(() => setNewApplicationsMessage(null), 4000);
    } catch (err) {
      console.error('Error refreshing applications:', err);
      setError('Error al actualizar las postulaciones');
    } finally {
      setRefreshing(false);
    }
  };

  const loadAlertStats = async () => {
    if (!canAccessAlerts(user)) return;
    try {
      const stats = await adminService.getAlertStats();
      setAlertStats(stats);
    } catch {
      // non-critical
    }
  };

  // Load alerts for superadmins
  const loadAlerts = async () => {
    if (!canAccessAlerts(user)) {
      return;
    }

    try {
      setAlertsLoading(true);
      const response = await adminService.getAlerts(
        {
          ...alertsFilters,
          applicationId: alertsSearchTerm || undefined,
        },
        alertsPagination.page,
        alertsPagination.limit
      );
      setAlerts(response.data);
      setAlertsPagination({
        page: response.pagination.page,
        limit: response.pagination.limit,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages,
      });
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : 'Error al cargar alertas',
        type: 'error',
      });
    } finally {
      setAlertsLoading(false);
    }
  };

  const handleAlertsFilterChange = (key: keyof AlertFilters, value: string) => {
    setAlertsFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
    }));
  };

  const handleAlertsTypesChange = (types: AlertType[]) => {
    setAlertsFilters({
      ...alertsFilters,
      alertTypes: types.length > 0 ? types : undefined,
    });
  };

  const handleClearAlertsFilters = () => {
    setAlertsFilters({});
    setAlertsSearchTerm('');
  };

  const handleAlertsPageChange = (page: number) => {
    setAlertsPagination((prev) => ({ ...prev, page }));
  };

  const handleResolveAlert = async (
    alertId: string,
    resolutionNote: string
  ) => {
    try {
      await adminService.resolveAlert(alertId, resolutionNote);
      setToast({
        message: 'Alerta resuelta exitosamente',
        type: 'success',
      });
      loadAlerts();
      loadAlertStats();
    } catch (err) {
      setToast({
        message:
          err instanceof Error ? err.message : 'Error al resolver la alerta',
        type: 'error',
      });
    }
  };

  const handleDismissAlert = async (alertId: string, note: string) => {
    try {
      await adminService.dismissAlert(alertId, note);
      setToast({
        message: 'Alerta descartada exitosamente',
        type: 'success',
      });
      loadAlerts();
      loadAlertStats();
    } catch (err) {
      setToast({
        message:
          err instanceof Error ? err.message : 'Error al descartar la alerta',
        type: 'error',
      });
    }
  };

  const handleFolioClick = (folio: string) => {
    setSearchTerm(folio);
    setAlertsDrawerOpen(false);
    const applicationsSection = document.getElementById('applications-list');
    if (applicationsSection) {
      applicationsSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const handleCreateAlert = async (data: CreateAlertData) => {
    await adminService.createAlert(data);
    setToast({ message: 'Alerta creada exitosamente', type: 'success' });
    loadAlerts();
    loadAlertStats();
  };

  useEffect(() => {
    if (templateId) {
      adminService.getContest(templateId).then((c) => {
        if (c) {
          setContestName(c.year ? `${c.name} ${c.year}` : c.name);
          setContestDeadline(c.deadline);
          setContestIsActive(c.isActive);
        }
      });
    }
    loadFilterOptions();
    loadApplications();
    loadSupplementaryStats();
    adminService.checkAndGenerateAlerts().then(() => {
      loadAlerts();
      loadAlertStats();
    }).catch(() => {
      loadAlerts();
      loadAlertStats();
    });

    // BACKEND NOTE (AUTO-REFRESH): The manual "Actualizar" button was removed by design decision.
    // The dashboard auto-refreshes every 5 minutes in the background. Rationale: this is an
    // operational monitoring dashboard, not a real-time trading screen. Evaluations and
    // admissibility decisions change on a timescale of hours, not seconds. A visible refresh
    // button implies data can silently go stale, which erodes trust without adding value.
    // On the backend, ensure GET /applications and GET /stats respond in < 500 ms under normal
    // load so the silent refresh is imperceptible to the user.
    const AUTO_REFRESH_MS = 5 * 60 * 1000;
    const intervalId = setInterval(() => {
      loadApplications();
      loadSupplementaryStats();
    }, AUTO_REFRESH_MS);
    return () => clearInterval(intervalId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload applications when search term changes (with debouncing)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== undefined) {
        loadApplications();
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload alerts when filters change (with debouncing)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadAlerts();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [alertsFilters, alertsSearchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload alerts when pagination changes
  useEffect(() => {
    loadAlerts();
  }, [alertsPagination.page]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load funnel + pool stats on mount (shown in Estado del proceso, always visible)
  useEffect(() => {
    Promise.all([
      adminService.getAdmissibilityFunnel(templateId),
      adminService.getEvaluationFunnel(templateId),
      adminService.getAssignmentPoolStats(templateId),
    ]).then(([adm, ev, pool]) => {
      setAdmFunnel(adm);
      setEvalFunnel(ev);
      setPoolStats(pool);
    }).catch(() => {/* non-critical */});
  }, [templateId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load reports + tableros when reportes tab is first activated
  useEffect(() => {
    if (activeTab !== 'reportes' || reportsLoaded) return;
    Promise.all([
      adminService.getEvaluatorAssignmentReport(templateId),
      adminService.getRejectedAssignments(templateId),
      adminService.getOverdueEvaluations(7, templateId),
      adminService.getInactiveEvaluatorReport(templateId),
      adminService.getOecdDistribution(templateId),
      adminService.getArbitrationApplications(templateId),
      adminService.getInstitutionReport(templateId),
      adminService.getProjectCompositionReport(templateId),
      adminService.getBudgetTotals(templateId),
    ]).then(([r10, r11, r12, r13, ocde, arb, inst, comp, bud]) => {
      setRep010(r10);
      setRep011(r11);
      setRep012(r12);
      setRep013(r13);
      setOecdDist(ocde);
      setArbitrationApps(arb);
      setInstReport(inst);
      setCompReport(comp);
      setBudgetTotals(bud);
      setReportsLoaded(true);
    }).catch(() => {/* non-critical */});
  }, [activeTab, reportsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load evaluador dashboard when evaluadores tab is first activated
  useEffect(() => {
    if (activeTab !== 'evaluaciones' || evaluadoresLoaded) return;
    setEvaluadoresLoaded(true);
    adminService.getEvaluatorDashboard(templateId).then(setEvalDashboard).catch(() => {/* non-critical */});
  }, [activeTab, evaluadoresLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle automatic evaluation view when application_number is in URL
  useEffect(() => {
    const applicationNumber = searchParams.get('application_number');
    if (applicationNumber && applications.length > 0 && !loading) {
      // Find the application with the matching application number
      const targetApplication = applications.find(
        (app) => app.applicationNumber === applicationNumber
      );

      if (targetApplication) {
        // Automatically trigger handleViewEvaluations
        handleViewEvaluations(targetApplication);

        // Remove the application_number parameter from URL to prevent repeated triggers
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('application_number');
        setSearchParams(newSearchParams, { replace: true });
      }
    }
  }, [applications, loading, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAssignReview = async (applicationId: string) => {
    if (!user) return;

    try {
      // Save scroll position
      const scrollPosition = window.scrollY;

      await adminService.assignReviewer(
        applicationId,
        user.id,
        user.name
      );

      // Reload applications to reflect the change and update counters
      await loadApplications();

      // Restore scroll position after a brief delay to ensure DOM is updated
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 0);

      setToast({
        message: 'Revisión asignada exitosamente',
        type: 'success',
      });
    } catch (err) {
      setToast({
        message:
          err instanceof Error ? err.message : 'Error al asignar revisión',
        type: 'error',
      });
    }
  };

  const handleMarkAdmissible = async (applicationId: string) => {
    if (!user) return;

    // eslint-disable-next-line no-restricted-globals
    const confirmed = confirm(
      '¿Está seguro de que desea marcar esta postulación como admisible?'
    );
    if (!confirmed) return;

    try {
      // Save scroll position
      const scrollPosition = window.scrollY;

      await adminService.markAdmissible(
        applicationId,
        user.id,
        user.name
      );

      // Reload applications to reflect the change and update counters
      await loadApplications();

      // Restore scroll position
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 0);

      setToast({
        message: 'Postulación marcada como admisible exitosamente',
        type: 'success',
      });
    } catch (err) {
      setToast({
        message:
          err instanceof Error ? err.message : 'Error al marcar como admisible',
        type: 'error',
      });
    }
  };

  const handleMarkNotAdmissible = async (applicationId: string) => {
    if (!user) return;

    const reason = prompt('Motivo del rechazo (requerido):');
    if (!reason) {
      setToast({
        message: 'Debe proporcionar un motivo para marcar como no admisible',
        type: 'warning',
      });
      return;
    }

    try {
      // Save scroll position
      const scrollPosition = window.scrollY;

      await adminService.markNotAdmissible(
        applicationId,
        user.id,
        user.name,
        reason
      );

      // Reload applications to reflect the change and update counters
      await loadApplications();

      // Restore scroll position
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 0);

      setToast({
        message: 'Postulación marcada como no admisible exitosamente',
        type: 'success',
      });
    } catch (err) {
      setToast({
        message:
          err instanceof Error
            ? err.message
            : 'Error al marcar como no admisible',
        type: 'error',
      });
    }
  };

  const handleMarkEvaluated = async (applicationId: string) => {
    if (!user) return;

    // Check permissions
    if (!canUserPerformAction('markEvaluated', user)) {
      setToast({
        message: 'No tiene permisos para realizar esta acción',
        type: 'error',
      });
      return;
    }

    // eslint-disable-next-line no-restricted-globals
    const confirmed = confirm(
      '¿Está seguro de que desea marcar esta postulación como evaluada?'
    );
    if (!confirmed) return;

    try {
      // Save scroll position
      const scrollPosition = window.scrollY;

      await adminService.markEvaluated(applicationId);

      // Reload applications to reflect the change and update counters
      await loadApplications();

      // Restore scroll position
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 0);

      setToast({
        message: 'Postulación marcada como evaluada exitosamente',
        type: 'success',
      });
    } catch (err) {
      setToast({
        message:
          err instanceof Error ? err.message : 'Error al marcar como evaluada',
        type: 'error',
      });
    }
  };

  const handleMarkSelected = async (applicationId: string) => {
    if (!user) return;

    // eslint-disable-next-line no-restricted-globals
    const confirmed = confirm(
      '¿Está seguro de que desea marcar esta postulación como adjudicada?'
    );
    if (!confirmed) return;

    try {
      // Save scroll position
      const scrollPosition = window.scrollY;

      await adminService.updateApplicationStatus(applicationId, 'selected');

      // Reload applications to reflect the change
      await loadApplications();

      // Restore scroll position
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 0);

      setToast({
        message: 'Postulación marcada como adjudicada exitosamente',
        type: 'success',
      });
    } catch (err) {
      setToast({
        message:
          err instanceof Error
            ? err.message
            : 'Error al marcar como adjudicada',
        type: 'error',
      });
    }
  };

  const handleMarkNotSelected = async (applicationId: string) => {
    if (!user) return;

    // eslint-disable-next-line no-restricted-globals
    const confirmed = confirm(
      '¿Está seguro de que desea marcar esta postulación como no-adjudicada?'
    );
    if (!confirmed) return;

    try {
      // Save scroll position
      const scrollPosition = window.scrollY;

      await adminService.updateApplicationStatus(applicationId, 'not_selected');

      // Reload applications to reflect the change
      await loadApplications();

      // Restore scroll position
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 0);

      setToast({
        message: 'Postulación marcada como no-adjudicada exitosamente',
        type: 'success',
      });
    } catch (err) {
      setToast({
        message:
          err instanceof Error
            ? err.message
            : 'Error al descartar adjudicación',
        type: 'error',
      });
    }
  };

  const handleRevertStatus = async (applicationId: string) => {
    if (!user) return;

    const application = applications.find((app) => app.id === applicationId);
    if (!application) {
      setToast({
        message: 'Aplicación no encontrada',
        type: 'error',
      });
      return;
    }

    const targetStatus = getRevertTargetStatus(application.status);
    if (!targetStatus) {
      setToast({
        message: 'Este estado no puede ser revertido',
        type: 'warning',
      });
      return;
    }

    const confirmationMessage = getRevertConfirmationMessage(
      application.status,
      targetStatus
    );
    // eslint-disable-next-line no-restricted-globals
    const confirmed = confirm(confirmationMessage);
    if (!confirmed) return;

    // Ask for optional notes
    const notes = prompt('Notas sobre la reversión (opcional):');

    try {
      // Save scroll position
      const scrollPosition = window.scrollY;

      await adminService.revertApplicationStatus(
        applicationId,
        notes || undefined
      );

      // Reload applications to reflect the change and update counters
      await loadApplications();

      // Restore scroll position
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 0);

      setToast({
        message: 'Estado revertido exitosamente',
        type: 'success',
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? getRevertErrorMessage((err as any).status) || err.message
          : 'Error al revertir el estado';

      setToast({
        message: errorMessage,
        type: 'error',
      });
    }
  };

  const handleManageEvaluators = (application: ApplicationWithAdminData) => {
    // Check permissions
    if (!canUserPerformAction('manageEvaluators', user)) {
      setToast({
        message: 'No tiene permisos para realizar esta acción',
        type: 'error',
      });
      return;
    }

    setEvaluatorModal({
      isOpen: true,
      application,
    });
  };

  const handleCloseEvaluatorModal = () => {
    setEvaluatorModal({
      isOpen: false,
      application: null,
    });
  };

  const handleEvaluatorAssignmentComplete = () => {
    // Reload applications to reflect changes
    loadApplications();
  };

  const handleViewEvaluations = (application: ApplicationWithAdminData) => {
    // Check permissions
    if (!canUserPerformAction('viewEvaluations', user)) {
      setToast({
        message: 'No tiene permisos para realizar esta acción',
        type: 'error',
      });
      return;
    }

    setEvaluationsModal({
      isOpen: true,
      application,
    });
  };

  const handleCloseEvaluationsModal = () => {
    setEvaluationsModal({
      isOpen: false,
      application: null,
    });
  };

  // Opens the evaluations modal given just an applicationId (used by the board).
  const handleViewEvaluationsById = (applicationId: string) => {
    const app = applications.find((a) => a.id === applicationId);
    if (app) {
      handleViewEvaluations(app);
    } else {
      // Fetch and open
      adminService.getApplicationById(applicationId).then((a) => {
        handleViewEvaluations(a);
      }).catch(() => {/* non-critical */});
    }
  };

  const handleOpenNotifyModal = async () => {
    if (!canUserPerformAction('viewEvaluationStatuses', user)) {
      setToast({
        message: 'No tiene permisos para realizar esta acción',
        type: 'error',
      });
      return;
    }

    try {
      const stats = await adminService.getNotificationStats(templateId);
      setNotifyModal({
        isOpen: true,
        stats,
      });
    } catch (err) {
      setToast({
        message:
          err instanceof Error
            ? err.message
            : 'Error al cargar las estadísticas',
        type: 'error',
      });
    }
  };

  const handleCloseNotifyModal = () => {
    setNotifyModal({
      isOpen: false,
      stats: null,
    });
  };

  const handleConfirmNotification = async (
    applicationIds: string[]
  ): Promise<NotificationResult> => {
    try {
      const result = await adminService.notifySelectedAndNotSelected({
        applicationIds,
      });
      setToast({
        message: 'Notificaciones enviadas exitosamente',
        type: 'success',
      });
      // Reload applications to show updated awardNotifiedAt
      await loadApplications();
      return result;
    } catch (err) {
      setToast({
        message:
          err instanceof Error
            ? err.message
            : 'Error al enviar las notificaciones',
        type: 'error',
      });
      throw err;
    }
  };


  const handleDownloadPdf = async (applicationId: string) => {
    try {
      const blob = await applicationService.downloadApplicationPdf(
        applicationId,
        true
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `application-${applicationId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setToast({
        message: 'Error al descargar el PDF. Por favor, inténtalo de nuevo.',
        type: 'error',
      });
    }
  };

  const handleGlobalExport = async (statuses: string[], academicUnits: string[]) => {
    setShowExportModal(false);
    try {
      setExporting(true);
      await adminService.exportApplications({
        statuses: statuses.length > 0 ? statuses : undefined,
        academicUnits: academicUnits.length > 0 ? academicUnits : undefined,
        limit: 1000,
      });
      setToast({
        message: 'Exportación completada exitosamente',
        type: 'success',
      });
    } catch (err) {
      setToast({
        message:
          err instanceof Error ? err.message : 'Error al exportar aplicaciones',
        type: 'error',
      });
    } finally {
      setExporting(false);
    }
  };

  const openExportModal = () => {
    setExportStatuses([]);
    setExportAcademicUnits([]);
    setShowExportModal(true);
  };


  const handleEvaluationsExport = async () => {
    try {
      setExporting(true);
      await adminService.exportEvaluations();
      setToast({
        message: 'Exportación completada exitosamente',
        type: 'success',
      });
    } catch (err) {
      setToast({
        message:
          err instanceof Error ? err.message : 'Error al exportar evaluaciones',
        type: 'error',
      });
    } finally {
      setExporting(false);
    }
  };

  const handleStatusExport = async (status: string) => {
    try {
      setExporting(true);
      await adminService.exportApplications({ status, limit: 1000 });
      setToast({
        message: 'Exportación completada exitosamente',
        type: 'success',
      });
    } catch (err) {
      setToast({
        message:
          err instanceof Error ? err.message : 'Error al exportar aplicaciones',
        type: 'error',
      });
    } finally {
      setExporting(false);
    }
  };

  const getEvalStatus = (a: EvaluatorAssignment): string => {
    if (a.conflictStatus === 'conflict_declared') return 'Conflicto declarado';
    if (a.status === 'completed') return 'Completada';
    if (a.conflictStatus === 'no_conflict') return 'Pendiente';
    return 'Declaración pendiente';
  };

  // For non-superadmins, map under_evaluation and evaluated to admissible for display
  const displayApplications = useMemo(() => {
    const isSuperAdmin =
      user?.role === 'admin' && user?.userProfile?.affiliation === 'TODAS';

    if (isSuperAdmin) {
      return applications;
    }

    // For non-superadmins, group evaluation-related statuses under 'admissible'
    return applications.map((app) => {
      if (app.status === 'under_evaluation' || app.status === 'evaluated') {
        return { ...app, status: 'admissible' as const };
      }
      return app;
    });
  }, [applications, user]);

  const filteredApplications = useMemo(() => {
    let apps = displayApplications;
    const { unidadAcademica, evaluationStatus, evaluador, researcherType, evaluatorStatus } = globalFilters;

    if (unidadAcademica.length > 0) {
      apps = apps.filter((app) =>
        unidadAcademica.includes(app.user.userProfile?.unidadAcademica ?? '')
      );
    }
    if (evaluationStatus.length > 0) {
      apps = apps.filter((app) => {
        const assignments = app.evaluatorAssignments ?? [];
        return evaluationStatus.some((s) =>
          assignments.some((a) => getEvalStatus(a) === s)
        );
      });
    }
    if (evaluador.length > 0) {
      apps = apps.filter((app) =>
        (app.evaluatorAssignments ?? []).some((a) =>
          evaluador.includes(a.evaluatorId)
        )
      );
    }
    if (researcherType.length > 0) {
      apps = apps.filter((app) =>
        researcherType.includes(app.user.userProfile?.researcherType ?? '')
      );
    }
    if (evaluatorStatus.length > 0 && evaluatorStatus.length < 2) {
      const wantAssigned = evaluatorStatus[0] === 'assigned';
      apps = apps.filter((app) => {
        const hasEvaluator = (app.evaluatorAssignments ?? []).length > 0;
        return wantAssigned ? hasEvaluator : !hasEvaluator;
      });
    }
    return apps;
  }, [displayApplications, globalFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <div className={styles.container}>
        <PageHeader
          titleLevel={templateId ? 'detail' : 'page'}
          title={contestName || 'Panel de Administración'}
          backLink={{
            to: 'concursos',
            onClick: () => navigate('/admin/dashboard'),
          }}
        />
        <div className={styles.error}>
          <h2>Error</h2>
          <p>{error}</p>
          <Button onClick={loadApplications} variant="primary">
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <PageHeader
        titleLevel={templateId ? 'detail' : 'page'}
        title={contestName || 'Panel de Administración'}
        backLink={{
          to: 'concursos',
          onClick: () => navigate('/admin/dashboard'),
        }}      />

      <div className={styles.container}>
        {/* Fila-resumen compacta del estado; el detalle completo se abre en el
            modal "Ver detalle" (antes era un panel fijo alto que empujaba los tabs). */}
        <div className={styles.statsSummary}>
          <div className={styles.statsSummaryKpis}>
            <span className={styles.statsSummaryLabel}>Postulaciones:</span>
            <span className={styles.statsSummaryKpi}>
              <strong>{admFunnel.submitted}</strong> recibidas
            </span>
            <span className={styles.statsSummaryKpi}>
              <strong>{admFunnel.admissible}</strong> admisibles
            </span>
            <span className={styles.statsSummaryKpi}>
              <strong>{evalFunnel.evaluated}</strong> evaluadas
            </span>
            {canAccessAlerts(user) && (
              <>
                <span className={styles.statsSummaryDivider} aria-hidden="true" />
                <button
                  type="button"
                  className={`${styles.statsSummaryKpi} ${styles.statsSummaryKpiLink}`}
                  onClick={() => setAlertsDrawerOpen(true)}
                >
                  <strong>{alertStats.totalOpen}</strong>{' '}
                  <span className={styles.statsSummaryKpiLinkText}>alertas abiertas</span>
                </button>
              </>
            )}
          </div>
          <div className={styles.statsSummaryActions}>
            <button
              type="button"
              className={styles.verDetalleBtn}
              onClick={() => setShowEstadoModal(true)}
            >
              <span className={styles.verDetalleLabel}>Ver detalle</span>
              <i className="uc-icon icon-shape--rounded" aria-hidden="true">
                arrow_forward
              </i>
            </button>
          </div>
        </div>

        {/* Modal: detalle completo del "Estado del concurso". */}
        {showEstadoModal && (
          <>
            <div
              className={styles.estadoDrawerBackdrop}
              onClick={() => setShowEstadoModal(false)}
              aria-hidden="true"
            />
            <div
              className={styles.estadoDrawerPanel}
              role="dialog"
              aria-modal="true"
              aria-label="Estado del concurso"
            >
              <div className={styles.estadoDrawerHeader}>
                <h2 className={styles.estadoDrawerTitle}>Estado del concurso</h2>
                <button
                  type="button"
                  className={styles.estadoDrawerClose}
                  onClick={() => setShowEstadoModal(false)}
                  aria-label="Cerrar panel"
                >
                  <i className="uc-icon">close</i>
                </button>
              </div>
              <div className={styles.estadoDrawerBody}>
                <div className={styles.statsPanel}>
          {/* Estado del Concurso — visibilidad condicional por subproceso
              DEV NOTE: Cada fila se muestra solo cuando hay datos relevantes para el subproceso.
              En producción, estas condiciones deberían derivarse de la misma fuente de verdad
              que el estado del concurso (template.isActive, template.deadline, conteos reales).
              - showIngreso:      el concurso sigue abierto (deadline futuro Y isActive).
                                  Se oculta automáticamente cuando vence el plazo de postulación,
                                  sin requerir acción manual del administrador.
              - showAdmisibilidad: existen postulaciones recibidas (status != 'draft').
              - showEvaluacion:   existen postulaciones admisibles.
              - showFallo:        no quedan postulaciones por evaluar y hay al menos una admisible
                                  (es decir, el proceso de evaluación terminó y puede iniciarse el fallo).
          */}
          {(() => {
            const ingresoOpen = contestIsActive && (!contestDeadline || new Date(contestDeadline) > new Date());
            const showIngreso      = ingresoOpen && (admFunnel.drafts > 0 || admFunnel.submitted > 0);
            const showAdmisibilidad = admFunnel.submitted > 0;
            const showEvaluacion    = admFunnel.admissible > 0;
            const showFallo         = evalFunnel.admissible > 0 && evalFunnel.pendingEval === 0;
            if (!showIngreso && !showAdmisibilidad && !showEvaluacion && !showFallo) return null;
            return (
          <div className={styles.statsPanelSection}>
            <div className={styles.funnelProseSection}>
              {showIngreso && (<div className={styles.funnelRow}>
                <span className={styles.funnelLabel}>Ingreso</span>
                <p className={styles.funnelProse}>
                  Hay{' '}
                  <strong className={styles.funnelProseNum}>{admFunnel.drafts}</strong> borradores y{' '}
                  <strong className={styles.funnelProseNum}>{admFunnel.submitted}</strong> postulaciones recibidas.
                </p>
                <button
                  className={styles.funnelCopyBtn}
                  title="Copiar"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `Hay ${admFunnel.drafts} borradores y ${admFunnel.submitted} postulaciones recibidas.`
                    );
                    setCopiedFunnelLine('ingreso');
                    setTimeout(() => setCopiedFunnelLine(null), 2000);
                  }}
                >
                  <i className="uc-icon icon-size--sm">
                    {copiedFunnelLine === 'ingreso' ? 'check' : 'content_copy'}
                  </i>
                </button>
              </div>)}
              {showIngreso && showAdmisibilidad && <div className={styles.funnelProseDivider} />}
              {showAdmisibilidad && (<div className={styles.funnelRow}>
                <span className={styles.funnelLabel}>Admisibilidad</span>
                <p className={styles.funnelProse}>
                  De las{' '}
                  <span className={styles.funnelProseNumPrimary}>{admFunnel.submitted} postulaciones recibidas</span>
                  {', hay '}
                  <strong className={styles.funnelProseNum}>{admFunnel.pending}</strong> por revisar,{' '}
                  <strong className={styles.funnelProseNum}>{admFunnel.underReview}</strong> en revisión,{' '}
                  <strong className={styles.funnelProseNum}>{admFunnel.admissible}</strong> admisibles y{' '}
                  <strong className={styles.funnelProseNum}>{admFunnel.notAdmissible}</strong> no admisibles.
                </p>
                <button
                  className={styles.funnelCopyBtn}
                  title="Copiar"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `De las ${admFunnel.submitted} postulaciones recibidas, hay ${admFunnel.pending} por revisar, ${admFunnel.underReview} en revisión, ${admFunnel.admissible} admisibles y ${admFunnel.notAdmissible} no admisibles.`
                    );
                    setCopiedFunnelLine('adm');
                    setTimeout(() => setCopiedFunnelLine(null), 2000);
                  }}
                >
                  <i className="uc-icon icon-size--sm">
                    {copiedFunnelLine === 'adm' ? 'check' : 'content_copy'}
                  </i>
                </button>
              </div>)}
              {showAdmisibilidad && showEvaluacion && <div className={styles.funnelProseDivider} />}
              {showEvaluacion && (<div className={styles.funnelRow}>
                <span className={styles.funnelLabel}>Evaluación</span>
                <p className={styles.funnelProse}>
                  De las{' '}
                  <strong className={styles.funnelProseNum}>{evalFunnel.admissible}</strong> postulaciones admisibles, hay{' '}
                  <strong className={styles.funnelProseNum}>{evalFunnel.pendingEval}</strong> por evaluar,{' '}
                  <strong className={styles.funnelProseNum}>{evalFunnel.underEvaluation}</strong> en evaluación y{' '}
                  <strong className={styles.funnelProseNum}>{evalFunnel.evaluated}</strong> evaluadas.
                </p>
                <button
                  className={styles.funnelCopyBtn}
                  title="Copiar"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `De las ${evalFunnel.admissible} postulaciones admisibles, hay ${evalFunnel.pendingEval} por evaluar, ${evalFunnel.underEvaluation} en evaluación y ${evalFunnel.evaluated} evaluadas.`
                    );
                    setCopiedFunnelLine('eval');
                    setTimeout(() => setCopiedFunnelLine(null), 2000);
                  }}
                >
                  <i className="uc-icon icon-size--sm">
                    {copiedFunnelLine === 'eval' ? 'check' : 'content_copy'}
                  </i>
                </button>
              </div>)}
              {showEvaluacion && showFallo && <div className={styles.funnelProseDivider} />}
              {showFallo && (<div className={styles.funnelRow}>
                <span className={styles.funnelLabel}>Fallo</span>
                <p className={styles.funnelProse}>
                  De las{' '}
                  <strong className={styles.funnelProseNum}>{evalFunnel.evaluated}</strong> postulaciones evaluadas, hay{' '}
                  <strong className={styles.funnelProseNum}>{evalFunnel.awarded}</strong> adjudicadas y{' '}
                  <strong className={styles.funnelProseNum}>{evalFunnel.notAwarded}</strong> no adjudicadas.
                </p>
                <button
                  className={styles.funnelCopyBtn}
                  title="Copiar"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `De las ${evalFunnel.evaluated} postulaciones evaluadas, hay ${evalFunnel.awarded} adjudicadas y ${evalFunnel.notAwarded} no adjudicadas.`
                    );
                    setCopiedFunnelLine('fallo');
                    setTimeout(() => setCopiedFunnelLine(null), 2000);
                  }}
                >
                  <i className="uc-icon icon-size--sm">
                    {copiedFunnelLine === 'fallo' ? 'check' : 'content_copy'}
                  </i>
                </button>
              </div>)}
            </div>
          </div>
            );
          })()}
          <div className={styles.statsPanelDivider} />
          {/* Sección ALERTAS */}
          {canAccessAlerts(user) && (
            <div className={styles.statsPanelSection}>
              <div className={styles.funnelRow}>
                <span className={styles.funnelLabel}>Alertas</span>
                <p className={styles.funnelProse}>
                  <strong className={styles.funnelProseNum}>
                    {alertStats.totalOpen + alertStats.totalResolved + alertStats.totalDismissed}
                  </strong>{' '}en total:{' '}
                  <strong className={styles.alertNumOpen}>{alertStats.totalOpen}</strong> abiertas,{' '}
                  <strong className={styles.alertNumResolved}>{alertStats.totalResolved}</strong> resueltas y{' '}
                  <strong className={styles.alertNumDismissed}>{alertStats.totalDismissed}</strong> descartadas.
                </p>
              </div>
            </div>
          )}
                </div>
                {/* Aclaración del detalle de indicadores (justo bajo la tabla). */}
                <p className={styles.procesoModalIntro}>Las etapas del concurso se muestran según su avance: cada subproceso aparece solo cuando hay datos disponibles para él.</p>
                {/* Glosario "¿Qué significa cada estado?" (antes en el modal
                    "Saber más"), ahora dentro del drawer, debajo del detalle. */}
                <div className={styles.estadoGlosario}>
                  <h3 className={styles.estadoGlosarioTitle}>¿Qué significa cada estado?</h3>
                  <section className={styles.procesoSection}>
                    <h3 className={styles.procesoSectionTitle}>Admisibilidad</h3>
                    <ul className={styles.procesoList}>
                      <li>
                        <strong>Por revisar</strong> — postulaciones recibidas que aún no han sido asignadas a un revisor de admisibilidad.
                      </li>
                      <li>
                        <strong>En revisión</strong> — tienen revisor asignado y están siendo evaluadas en términos de requisitos formales.
                      </li>
                      <li>
                        <strong>Admisibles</strong> — aprobaron la revisión formal y están disponibles para iniciar el proceso de evaluación.
                      </li>
                      <li>
                        <strong>No admisibles</strong> — fueron rechazadas por no cumplir los requisitos formales del concurso.
                      </li>
                    </ul>
                  </section>
                  <section className={styles.procesoSection}>
                    <h3 className={styles.procesoSectionTitle}>Evaluación</h3>
                    <ul className={styles.procesoList}>
                      <li>
                        <strong>Por evaluar</strong> — postulaciones admisibles que aún no tienen evaluadores asignados, o cuyos evaluadores no han iniciado el proceso.
                      </li>
                      <li>
                        <strong>En evaluación</strong> — al menos un evaluador está activo. Incluye todos los estados intermedios del proceso:
                        <ul className={styles.procesoSublist}>
                          <li>Evaluación en curso o atrasada.</li>
                          <li>Desviación detectada entre puntajes: se requiere un árbitro que aún no ha sido asignado.</li>
                          <li>Árbitro invitado o en proceso de evaluación.</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Evaluadas</strong> — el proceso está completamente cerrado. Solo se cuentan aquí postulaciones con resultado <em>sin controversia</em> o <em>arbitraje finalizado</em>. Una postulación con arbitraje pendiente permanece en "En evaluación" hasta que el árbitro entregue su evaluación.
                      </li>
                    </ul>
                  </section>
                  <section className={styles.procesoSection}>
                    <h3 className={styles.procesoSectionTitle}>Fallo</h3>
                    <ul className={styles.procesoList}>
                      <li>
                        <strong>Fallo aún no iniciado</strong> — hay postulaciones evaluadas, pero el comité aún no ha emitido resolución. Ninguna ha sido adjudicada ni rechazada formalmente.
                      </li>
                      <li>
                        <strong>Adjudicadas</strong> — postulaciones seleccionadas para financiamiento. Son un subconjunto de las evaluadas.
                      </li>
                      <li>
                        <strong>No adjudicadas</strong> — postulaciones que completaron la evaluación pero no fueron seleccionadas, por no alcanzar el puntaje mínimo u otros criterios del concurso.
                      </li>
                    </ul>
                  </section>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Alerts drawer — rendered outside tab condition so it opens from any tab */}
        {canAccessAlerts(user) && (
          <AlertsDrawer
            isOpen={alertsDrawerOpen}
            onClose={() => setAlertsDrawerOpen(false)}
            alerts={alerts}
            isLoading={alertsLoading}
            pagination={alertsPagination}
            onPageChange={handleAlertsPageChange}
            filters={alertsFilters}
            onFilterChange={handleAlertsFilterChange}
            onTypesChange={handleAlertsTypesChange}
            onClearFilters={handleClearAlertsFilters}
            searchTerm={alertsSearchTerm}
            onSearchChange={setAlertsSearchTerm}
            onResolveAlert={handleResolveAlert}
            onDismissAlert={handleDismissAlert}
            onFolioClick={handleFolioClick}
            totalOpen={alertStats.totalOpen}
            totalResolved={alertStats.totalResolved}
            totalDismissed={alertStats.totalDismissed}
            typeCounts={alertStats.openByType}
            onCreateAlert={handleCreateAlert}
          />
        )}

        {/* Section tab nav */}
        <div className={styles.tabNav}>
          <button
            className={activeTab === 'postulaciones' ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab('postulaciones')}
          >
            Postulaciones
          </button>
          {canUserPerformAction('manageEvaluators', user) && (
            <button
              className={
                activeTab === 'asignacion-masiva' ? styles.tabActive : styles.tab
              }
              onClick={() => setActiveTab('asignacion-masiva')}
            >
              Asignación masiva
            </button>
          )}
          <button
            className={activeTab === 'evaluaciones' ? styles.tabActive : styles.tab}
            onClick={() => {
              setFocusPendingInvitations(false);
              setActiveTab('evaluaciones');
            }}
          >
            Evaluaciones
          </button>
          <button
            className={activeTab === 'evaluadores' ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab('evaluadores')}
          >
            Evaluadores
          </button>
          <button
            className={activeTab === 'reportes' ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab('reportes')}
          >
            Reportes
          </button>
        </div>

        {/* Postulaciones tab */}
        {activeTab === 'postulaciones' && (
          <>
            <div className={styles.listHeader} id="applications-list">
              <div className={styles.sectionHeaderTop}>

                <div className={styles.sectionHeaderActions}>
                  <div className={styles.searchContainer}>
                    <svg
                      className={styles.searchIcon}
                      width="15"
                      height="15"
                      viewBox="0 0 15 15"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M10.4167 9.16667H9.75833L9.525 8.94167C10.3417 7.99167 10.8333 6.75833 10.8333 5.41667C10.8333 2.425 8.40833 0 5.41667 0C2.425 0 0 2.425 0 5.41667C0 8.40833 2.425 10.8333 5.41667 10.8333C6.75833 10.8333 7.99167 10.3417 8.94167 9.525L9.16667 9.75833V10.4167L13.3333 14.575L14.575 13.3333L10.4167 9.16667ZM5.41667 9.16667C3.34167 9.16667 1.66667 7.49167 1.66667 5.41667C1.66667 3.34167 3.34167 1.66667 5.41667 1.66667C7.49167 1.66667 9.16667 3.34167 9.16667 5.41667C9.16667 7.49167 7.49167 9.16667 5.41667 9.16667Z"
                        fill="currentColor"
                      />
                    </svg>
                    <input
                      type="text"
                      placeholder="Buscar por folio, nombre, RUN, unidad académica o palabra clave"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={styles.searchInput}
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        className={styles.clearSearchButton}
                        aria-label="Limpiar búsqueda"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <div className={styles.exportActions}>
                  <Button
                    onClick={openExportModal}
                    variant="text"
                    size="sm"
                    disabled={loading || exporting}
                  >
                    {exporting ? 'Generando...' : 'Descargar Postulaciones'}
                    <i className="uc-icon icon-size--sm" style={{ marginLeft: '0.25rem' }}>open_in_new</i>
                  </Button>
                  {canDownloadEvaluations(user) && (
                    <Button
                      onClick={handleEvaluationsExport}
                      variant="text"
                      size="sm"
                      disabled={loading || exporting}
                    >
                      <i className="uc-icon icon-size--sm">download</i>
                      {exporting ? 'Generando...' : 'Descargar Evaluaciones'}
                    </Button>
                  )}
                  <button
                    className={styles.refreshButton}
                    onClick={handleRefreshApplications}
                    disabled={refreshing}
                    title="Actualizar postulaciones"
                  >
                    <i className="uc-icon icon-size--sm">
                      {refreshing ? 'cached' : 'refresh'}
                    </i>
                  </button>
                  </div>
                </div>
              </div>
              <AdvancedFilters
                filters={globalFilters}
                onChange={setGlobalFilters}
                options={filterOptions}
              />
            </div>

            <ApplicationListTable
                applications={filteredApplications}
                groupByStatus={true}
                onAssignReview={handleAssignReview}
                onMarkAdmissible={handleMarkAdmissible}
                onMarkNotAdmissible={handleMarkNotAdmissible}
                onMarkEvaluated={handleMarkEvaluated}
                onMarkSelected={handleMarkSelected}
                onMarkNotSelected={handleMarkNotSelected}
                onRevertStatus={handleRevertStatus}
                onManageEvaluators={handleManageEvaluators}
                onViewEvaluations={handleViewEvaluations}
                onDownloadPdf={handleDownloadPdf}
                currentUserId={user?.id}
                currentUser={user}
                isLoading={loading}
                statusPagination={statusPagination}
                onStatusPageChange={handleStatusPageChange}
                onStatusPageSizeChange={handleStatusPageSizeChange}

                onExportStatus={handleStatusExport}
                searchTerm={searchTerm}
                visibleStatuses={statusesToLoad}
              />
          </>
        )}

        {/* Asignación masiva tab — importación del archivo de match */}
        {activeTab === 'asignacion-masiva' &&
          canUserPerformAction('manageEvaluators', user) && (
            <BulkAssignmentTab
              formTemplateId={templateId ?? null}
              onGoToApplications={() => setActiveTab('postulaciones')}
              onGoToEvaluations={() => {
                setFocusPendingInvitations(false);
                setActiveTab('evaluaciones');
              }}
              onGoToPendingInvitations={() => {
                setFocusPendingInvitations(true);
                setActiveTab('evaluaciones');
              }}
              onImported={loadApplications}
            />
          )}

        {/* Evaluaciones tab — Tablero de Control de Evaluaciones */}
        {activeTab === 'evaluaciones' && (
          <EvaluationsDashboard
            formTemplateId={templateId ?? null}
            onViewEvaluations={handleViewEvaluationsById}
            focusPendingInvitations={focusPendingInvitations}
          />
        )}

        {/* Evaluadores tab */}
        {activeTab === 'evaluadores' && (
          <EvaluadoresTab
            formTemplateId={templateId ?? null}
            onToast={(msg) => setToast({ message: msg, type: 'success' })}
          />
        )}

        {/* Reportes tab */}
        {activeTab === 'reportes' && (
          <div className={styles.content}>



            {/* REP-UA: Postulaciones por unidad académica */}
            {uaByFaculty.length > 0 && (
              <div className={styles.reportSection}>
                <div className={styles.reportSectionHeader}>
                  <div>
                    <h3>Postulaciones por unidad académica</h3>
                    <p>Distribución de postulaciones recibidas según la unidad académica del investigador responsable.</p>
                  </div>
                  <div className={styles.viewToggleRow}>
                    <div className={styles.viewToggle}>
                      <button
                        className={uaView === 'chart' ? styles.viewToggleActive : styles.viewToggleBtn}
                        onClick={() => setUaView('chart')}
                        aria-label="Ver gráfico"
                        title="Gráfico"
                      >
                        <i className="uc-icon">bar_chart</i>
                      </button>
                      <button
                        className={uaView === 'table' ? styles.viewToggleActive : styles.viewToggleBtn}
                        onClick={() => setUaView('table')}
                        aria-label="Ver tabla"
                        title="Tabla"
                      >
                        <i className="uc-icon">table_rows</i>
                      </button>
                    </div>
                    <Button
                      variant="text"
                      size="sm"
                      onClick={() => {
                        const rows = [
                          'Facultad,Unidad Académica,Postulaciones',
                          ...uaByFaculty.flatMap((fac) =>
                            fac.units.map((u) => `"${fac.faculty}","${u.name}",${u.count}`)
                          ),
                        ];
                        const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'postulaciones_por_ua.csv';
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                    >
                      <i className="uc-icon icon-size--sm">download</i>
                      Descargar CSV
                    </Button>
                  </div>
                </div>

                {uaView === 'chart' ? (() => {
                  const maxCount = Math.max(...uaByFaculty.flatMap(f => f.units.map(u => u.count)), 1);
                  return (
                    <div className={styles.tableroCard}>
                      <div className={styles.uaChartRows}>
                        {uaByFaculty.map((fac) => (
                          <React.Fragment key={fac.faculty}>
                            <div className={styles.uaFacultyRow}>
                              <span className={styles.uaFacultyLabel}>{fac.faculty} ({fac.total})</span>
                            </div>
                            {fac.units.map((ua) => (
                              <div key={ua.name} className={styles.uaUnitRow}>
                                <span className={styles.uaUnitLabel}>{ua.name}</span>
                                <div className={styles.tableroBarWrap}>
                                  <div className={styles.tableroBar} style={{ width: `${Math.round((ua.count / maxCount) * 100)}%` }} />
                                </div>
                                <span className={styles.tableroBarCount}>{ua.count}</span>
                              </div>
                            ))}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  );
                })() : (
                  <div className={styles.reportTableWrap}>
                    <table className={styles.reportTable}>
                      <thead>
                        <tr>
                          <th>Facultad</th>
                          <th>Unidad académica</th>
                          <th className={styles.num}>Postulaciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uaByFaculty.flatMap((fac) =>
                          fac.units.map((ua, i) => (
                            <tr key={`${fac.faculty}-${ua.name}`}>
                              <td>{i === 0 ? `${fac.faculty} (${fac.total})` : ''}</td>
                              <td>{ua.name}</td>
                              <td className={styles.num}>{ua.count}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* REP-COMP: Caracterización de postulaciones */}
            <div className={styles.reportSection}>
              <div className={styles.reportSectionHeader}>
                <div>
                  <h3>Caracterización de postulaciones</h3>
                  <p>
                    <strong>{compReport.total}</strong> proyectos enviados — categoría, género e integrantes de equipo.
                  </p>
                </div>
                <Button variant="text" size="sm" disabled={compReport.total === 0} onClick={() => adminService.exportProjectCompositionReport(compReport)}>
                  <i className="uc-icon icon-size--sm">download</i>
                  Descargar CSV
                </Button>
              </div>
              {/* Categoría de proyectos */}
              <p className={styles.compSubSectionLabel}>Categoría de proyectos</p>
              <div className={styles.compGrid2}>
                {[
                  { value: compReport.consolidated, label: 'Cat. A — Consolidado' },
                  { value: compReport.emerging, label: 'Cat. B — Emergente' },
                ].map((item) => (
                  <div key={item.label} className={styles.compCard}>
                    <span className={styles.compCardValue}>{item.value}</span>
                    <span className={styles.compCardLabel}>{item.label}</span>
                  </div>
                ))}
              </div>
              {/* Participaciones en equipo por rol */}
              <p className={styles.compSubSectionLabel}>Participaciones en equipo</p>
              <div className={styles.compTeamStats}>
                <div className={styles.compTeamStatsRow}>
                  <span className={styles.compTeamStatLabel}>Total</span>
                  <span className={styles.compTeamStatValue}>{compReport.totalPeople}</span>
                </div>
                <div className={styles.compTeamStatsDivider} />
                <div className={styles.compTeamStatsRow}>
                  <span className={styles.compTeamStatLabel}>IR</span>
                  <span className={styles.compTeamStatValue}>{compReport.irCount}</span>
                </div>
                <div className={styles.compTeamStatsRow}>
                  <span className={styles.compTeamStatLabel}>IP</span>
                  <span className={styles.compTeamStatValue}>{compReport.ipCount}</span>
                </div>
                <div className={styles.compTeamStatsRow}>
                  <span className={styles.compTeamStatLabel}>IA</span>
                  <span className={styles.compTeamStatValue}>{compReport.iaCount}</span>
                </div>
                <div className={styles.compTeamStatsRow}>
                  <span className={styles.compTeamStatLabel}>COI</span>
                  <span className={styles.compTeamStatValue}>{compReport.coiCount}</span>
                </div>
                <div className={styles.compTeamStatsDivider} />
                <div className={styles.compTeamStatsRow}>
                  <span className={styles.compTeamStatLabel}>Hombres</span>
                  <span className={styles.compTeamStatValue}>{compReport.menTotal}</span>
                </div>
                <div className={styles.compTeamStatsRow}>
                  <span className={styles.compTeamStatLabel}>Mujeres</span>
                  <span className={styles.compTeamStatValue}>{compReport.womenTotal}</span>
                </div>
              </div>
              {/* Perfil de investigadores (IR e IP) */}
              <p className={styles.compSubSectionLabel}>Perfil IR e IP</p>
              <div className={styles.compProfileTable}>
                <div className={styles.compProfileCorner} />
                <div className={styles.compProfileHeadCell}>Cat. A — Consolidado</div>
                <div className={styles.compProfileHeadCell}>Cat. B — Emergente</div>
                <div className={styles.compProfileLabelCell}>IR</div>
                <div className={styles.compProfileValueCell}>{compReport.irConsolidated}</div>
                <div className={styles.compProfileValueCell}>{compReport.irEmerging}</div>
                <div className={styles.compProfileLabelCell}>IP</div>
                <div className={styles.compProfileValueCell}>{compReport.ipConsolidated}</div>
                <div className={styles.compProfileValueCell}>{compReport.ipEmerging}</div>
              </div>
              {/* Personas únicas */}
              <p className={styles.compSubSectionLabel}>Personas únicas</p>
              <div className={styles.compTeamStats}>
                <div className={styles.compTeamStatsRow}>
                  <span className={styles.compTeamStatLabel}>Total personas distintas</span>
                  <span className={styles.compTeamStatValue}>{compReport.uniquePeople}</span>
                </div>
                <div className={styles.compTeamStatsDivider} />
                <div className={styles.compTeamStatsRow}>
                  <span className={styles.compTeamStatLabel}>Hombres</span>
                  <span className={styles.compTeamStatValue}>{compReport.uniqueMen}</span>
                </div>
                <div className={styles.compTeamStatsRow}>
                  <span className={styles.compTeamStatLabel}>Mujeres</span>
                  <span className={styles.compTeamStatValue}>{compReport.uniqueWomen}</span>
                </div>
              </div>
            </div>

            {/* REP-TEAM-COMP: Composición de equipos */}
            <div className={styles.reportSection}>
              <div className={styles.reportSectionHeader}>
                <div>
                  <h3>Composición de equipos</h3>
                  <p>Estructura UC del equipo y participación de coinvestigadores externos.</p>
                </div>
                <div className={styles.viewToggleRow}>
                  <div className={styles.viewToggle}>
                    <button
                      className={teamCompView === 'chart' ? styles.viewToggleActive : styles.viewToggleBtn}
                      onClick={() => setTeamCompView('chart')}
                      aria-label="Ver gráfico"
                      title="Gráfico"
                    >
                      <i className="uc-icon">bar_chart</i>
                    </button>
                    <button
                      className={teamCompView === 'table' ? styles.viewToggleActive : styles.viewToggleBtn}
                      onClick={() => setTeamCompView('table')}
                      aria-label="Ver tabla"
                      title="Tabla"
                    >
                      <i className="uc-icon">table_rows</i>
                    </button>
                  </div>
                  <Button
                    variant="text"
                    size="sm"
                    disabled={compReport.total === 0}
                    onClick={() => {
                      const coiRows = [
                        { label: 'Sin coinvestigadores', value: compReport.coiNone },
                        { label: 'COI solo nacional', value: compReport.coiNationalOnly },
                        { label: 'COI solo internacional', value: compReport.coiInternationalOnly },
                        { label: 'COI nacional e internacional', value: compReport.coiBoth },
                      ];
                      const csv = [
                        'Sección,Combinación,Postulaciones',
                        ...compReport.ucCombinations.map((r) => `"Estructura UC","${r.label}",${r.projects}`),
                        ...coiRows.map((r) => `"Coinvestigadores","${r.label}",${r.value}`),
                      ];
                      const blob = new Blob([csv.join('\n')], { type: 'text/csv;charset=utf-8;' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'composicion_de_equipos.csv';
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                  >
                    <i className="uc-icon icon-size--sm">download</i>
                    Descargar CSV
                  </Button>
                </div>
              </div>

              {/* Sub-sección 1: Estructura UC */}
              <p className={styles.compSubSectionLabel}>Estructura UC del equipo</p>
              {(() => {
                const rows = compReport.ucCombinations.map((r) => ({ label: r.label, value: r.projects }));
                const max = Math.max(...rows.map((r) => r.value), 1);
                return teamCompView === 'chart' ? (
                  <div className={styles.tableroCard}>
                    <div className={styles.tableroChartRows}>
                      {rows.map((r) => (
                        <div key={r.label} className={styles.tableroChartRow}>
                          <span className={`${styles.tableroBarLabel} ${styles.compBarLabel}`}>{r.label}</span>
                          <div className={styles.tableroBarWrap}>
                            <div className={styles.tableroBar} style={{ width: `${Math.round((r.value / max) * 100)}%` }} />
                          </div>
                          <span className={styles.tableroBarCount}>{r.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className={styles.reportTableWrap}>
                    <table className={styles.reportTable}>
                      <thead><tr><th>Combinación UC</th><th className={styles.num}>Postulaciones</th></tr></thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr key={r.label}><td>{r.label}</td><td className={styles.num}>{r.value}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}

              {/* Sub-sección 2: Coinvestigadores */}
              <p className={styles.compSubSectionLabel} style={{ marginTop: 'var(--spacing-24)' }}>Coinvestigadores externos (COI)</p>
              {(() => {
                const coiRows = [
                  { label: 'Sin coinvestigadores', value: compReport.coiNone },
                  { label: 'Solo colaboración nacional', value: compReport.coiNationalOnly },
                  { label: 'Solo colaboración internacional', value: compReport.coiInternationalOnly },
                  { label: 'Nacional e internacional', value: compReport.coiBoth },
                ].filter((r) => r.value > 0);
                const max = Math.max(...coiRows.map((r) => r.value), 1);
                return teamCompView === 'chart' ? (
                  <div className={styles.tableroCard}>
                    <div className={styles.tableroChartRows}>
                      {coiRows.map((r) => (
                        <div key={r.label} className={styles.tableroChartRow}>
                          <span className={`${styles.tableroBarLabel} ${styles.compBarLabel}`}>{r.label}</span>
                          <div className={styles.tableroBarWrap}>
                            <div className={styles.tableroBar} style={{ width: `${Math.round((r.value / max) * 100)}%` }} />
                          </div>
                          <span className={styles.tableroBarCount}>{r.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className={styles.reportTableWrap}>
                    <table className={styles.reportTable}>
                      <thead><tr><th>Tipo de colaboración</th><th className={styles.num}>Postulaciones</th></tr></thead>
                      <tbody>
                        {coiRows.map((r) => (
                          <tr key={r.label}><td>{r.label}</td><td className={styles.num}>{r.value}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>

            {/* REP-OCDE: Distribución por área OCDE */}
            {oecdDist.length > 0 && (
              <div className={styles.reportSection}>
                <div className={styles.reportSectionHeader}>
                  <div>
                    <h3>Distribución por área OCDE</h3>
                    <p>Postulaciones recibidas por área de conocimiento OCDE del investigador responsable.</p>
                  </div>
                  <div className={styles.viewToggleRow}>
                    <div className={styles.viewToggle}>
                      <button
                        className={oecdView === 'chart' ? styles.viewToggleActive : styles.viewToggleBtn}
                        onClick={() => setOecdView('chart')}
                        aria-label="Ver gráfico"
                        title="Gráfico"
                      >
                        <i className="uc-icon">bar_chart</i>
                      </button>
                      <button
                        className={oecdView === 'table' ? styles.viewToggleActive : styles.viewToggleBtn}
                        onClick={() => setOecdView('table')}
                        aria-label="Ver tabla"
                        title="Tabla"
                      >
                        <i className="uc-icon">table_rows</i>
                      </button>
                    </div>
                    <Button
                      variant="text"
                      size="sm"
                      onClick={() => {
                        const rows = [
                          'Área OCDE,Postulaciones',
                          ...oecdDist.map((item) => `"${item.area}",${item.count}`),
                        ];
                        const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'postulaciones_por_area_ocde.csv';
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                    >
                      <i className="uc-icon icon-size--sm">download</i>
                      Descargar CSV
                    </Button>
                  </div>
                </div>

                {oecdView === 'chart' ? (
                  <div className={styles.tableroCard}>
                    <div className={styles.tableroChartRows}>
                      {oecdDist.map((item) => (
                        <div key={item.area} className={styles.tableroChartRow}>
                          <span className={styles.tableroBarLabel} title={item.area}>{item.area}</span>
                          <div className={styles.tableroBarWrap}>
                            <div className={styles.tableroBar} style={{ width: `${Math.round((item.count / oecdDist[0].count) * 100)}%` }} />
                          </div>
                          <span className={styles.tableroBarCount}>{item.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className={styles.reportTableWrap}>
                    <table className={styles.reportTable}>
                      <thead>
                        <tr>
                          <th>Área OCDE</th>
                          <th className={styles.num}>Postulaciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {oecdDist.map((item) => (
                          <tr key={item.area}>
                            <td>{item.area}</td>
                            <td className={styles.num}>{item.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* REP-INST: Instituciones participantes */}
            <div className={styles.reportSection}>
              <div className={styles.reportSectionHeader}>
                <div>
                  <h3>Instituciones participantes {instReport.totalUnique > 0 && `(${instReport.totalUnique})`}</h3>
                  <p>Instituciones únicas en equipos de postulación, desglosadas por país de origen.</p>
                </div>
                <div className={styles.viewToggleRow}>
                  <div className={styles.viewToggle}>
                    <button
                      className={instView === 'chart' ? styles.viewToggleActive : styles.viewToggleBtn}
                      onClick={() => setInstView('chart')}
                      aria-label="Ver gráfico"
                      title="Gráfico"
                    >
                      <i className="uc-icon">bar_chart</i>
                    </button>
                    <button
                      className={instView === 'table' ? styles.viewToggleActive : styles.viewToggleBtn}
                      onClick={() => setInstView('table')}
                      aria-label="Ver tabla"
                      title="Tabla"
                    >
                      <i className="uc-icon">table_rows</i>
                    </button>
                  </div>
                  <Button
                    variant="text"
                    size="sm"
                    disabled={instReport.byCountry.length === 0}
                    onClick={() => adminService.exportInstitutionReport(instReport.byCountry)}
                  >
                    <i className="uc-icon icon-size--sm">download</i>
                    Descargar CSV
                  </Button>
                </div>
              </div>
              <div className={styles.instCollabStat}>
                <span style={{ flex: 1 }}>
                  <strong className={styles.funnelProseNum}>{instReport.withInternationalCollab}</strong> postulaciones involucran al menos una institución extranjera, con participación de <strong className={styles.funnelProseNum}>{instReport.international}</strong> instituciones extranjeras únicas
                </span>
                <span className={styles.instCollabTooltipWrap}>
                  ?
                  <span className={styles.instCollabTooltip}>
                    Una postulación se cuenta como "con colaboración internacional" si al menos un integrante del equipo tiene asociada una institución extranjera. El conteo de instituciones únicas corresponde a affiliaciones distintas fuera de Chile.
                  </span>
                </span>
                <button
                  className={styles.funnelCopyBtn}
                  title="Copiar"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `${instReport.withInternationalCollab} postulaciones involucran al menos una institución extranjera, con participación de ${instReport.international} instituciones extranjeras únicas.`
                    );
                    setCopiedFunnelLine('inst');
                    setTimeout(() => setCopiedFunnelLine(null), 2000);
                  }}
                >
                  <i className="uc-icon icon-size--sm">
                    {copiedFunnelLine === 'inst' ? 'check' : 'content_copy'}
                  </i>
                </button>
              </div>
              {instReport.byCountry.length > 0 && (
                instView === 'chart' ? (() => {
                  const maxCount = Math.max(...instReport.byCountry.flatMap(c => c.institutions.map(i => i.count)), 1);
                  return (
                    <div className={styles.tableroCard} style={{ marginTop: '0.75rem' }}>
                      <div className={styles.uaChartRows}>
                        {instReport.byCountry.map((ctry) => (
                          <React.Fragment key={ctry.country}>
                            <div className={styles.uaFacultyRow}>
                              <span className={styles.uaFacultyLabel}>{ctry.country} ({ctry.total})</span>
                            </div>
                            {ctry.institutions.map((inst) => (
                              <div key={inst.name} className={styles.uaUnitRow}>
                                <span className={styles.uaUnitLabel}>{inst.name}</span>
                                <div className={styles.tableroBarWrap}>
                                  <div className={styles.tableroBar} style={{ width: `${Math.round((inst.count / maxCount) * 100)}%` }} />
                                </div>
                                <span className={styles.tableroBarCount}>{inst.count}</span>
                              </div>
                            ))}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  );
                })() : (
                  <div className={styles.reportTableWrap} style={{ marginTop: '0.75rem' }}>
                    <table className={styles.reportTable}>
                      <thead>
                        <tr>
                          <th>País</th>
                          <th>Institución</th>
                          <th className={styles.num}>Postulaciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {instReport.byCountry.flatMap((ctry) =>
                          ctry.institutions.map((inst, i) => (
                            <tr key={`${ctry.country}-${inst.name}`}>
                              <td>{i === 0 ? `${ctry.country} (${ctry.total})` : ''}</td>
                              <td>{inst.name}</td>
                              <td className={styles.num}>{inst.count}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </div>

            {/* Montos del presupuesto */}
            <div className={styles.reportSection}>
              <div className={styles.reportSectionHeader}>
                <div>
                  <h3>Montos del presupuesto</h3>
                  <p>Totales en pesos (CLP) de monto solicitado a Avanza UC y aportado por contrapartes, según estado de la postulación.</p>
                </div>
              </div>
              <div className={styles.reportTableWrap}>
                <table className={styles.reportTable}>
                  <thead>
                    <tr>
                      <th>Grupo</th>
                      <th className={styles.num}>Postulaciones</th>
                      <th className={styles.num}>Monto solicitado a Avanza UC (CLP)</th>
                      <th className={styles.num}>Monto aportado por contrapartes (CLP)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Borradores</td>
                      <td className={styles.num}>{budgetTotals.drafts.count}</td>
                      <td className={styles.num}>${budgetTotals.drafts.montoSolicitado.toLocaleString('es-CL')}</td>
                      <td className={styles.num}>${budgetTotals.drafts.montoContrapartes.toLocaleString('es-CL')}</td>
                    </tr>
                    <tr>
                      <td>Postulaciones recibidas</td>
                      <td className={styles.num}>{budgetTotals.submitted.count}</td>
                      <td className={styles.num}>${budgetTotals.submitted.montoSolicitado.toLocaleString('es-CL')}</td>
                      <td className={styles.num}>${budgetTotals.submitted.montoContrapartes.toLocaleString('es-CL')}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Descargar datos */}
            <div className={styles.reportSection}>
              <h3>Descargar datos</h3>
              <p>Descarga los datos de postulaciones y evaluaciones en formato CSV.</p>
              <div className={styles.reportActions}>
                <Button
                  onClick={openExportModal}
                  variant="text"
                  disabled={loading || exporting}
                >
                  <i className="uc-icon icon-size--sm">download</i>
                  {exporting ? 'Generando...' : 'Descargar Postulaciones'}
                </Button>
                {canDownloadEvaluations(user) && (
                  <Button
                    onClick={handleEvaluationsExport}
                    variant="text"
                    disabled={loading || exporting}
                  >
                    <i className="uc-icon icon-size--sm">download</i>
                    {exporting ? 'Generando...' : 'Descargar Evaluaciones'}
                  </Button>
                )}
              </div>
            </div>

            {canUserPerformAction('viewEvaluationStatuses', user) && (
              <div className={styles.reportSection}>
                <h3>Notificación de Resultados</h3>
                <p>
                  Enviar notificaciones por email a todos los postulantes con los
                  resultados de sus postulaciones (Adjudicado / No adjudicado).
                </p>
                <div className={styles.reportActions}>
                  <Button
                    variant="primary"
                    onClick={handleOpenNotifyModal}
                    disabled={loading}
                  >
                    Notificar resultados a postulantes
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      {newApplicationsMessage && (
        <Toast
          message={newApplicationsMessage}
          type={newApplicationsMessage.includes('nuevas') ? 'success' : 'info'}
          onClose={() => setNewApplicationsMessage(null)}
        />
      )}

      {evaluatorModal.application && (
        <EvaluatorAssignmentModal
          application={evaluatorModal.application}
          isOpen={evaluatorModal.isOpen}
          onClose={handleCloseEvaluatorModal}
          onAssignmentComplete={handleEvaluatorAssignmentComplete}
        />
      )}

      {evaluationsModal.application && (
        <EvaluationsModal
          applicationId={evaluationsModal.application.id}
          applicationNumber={evaluationsModal.application.applicationNumber}
          isOpen={evaluationsModal.isOpen}
          onClose={handleCloseEvaluationsModal}
        />
      )}

      {notifyModal.stats && (
        <NotifyResultsModal
          isOpen={notifyModal.isOpen}
          stats={notifyModal.stats}
          onClose={handleCloseNotifyModal}
          onConfirm={handleConfirmNotification}
        />
      )}

      {showUAModal && (
        <div
          className={styles.exportOverlay}
          onClick={() => setShowUAModal(false)}
        >
          <div
            className={styles.exportModal}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.exportModalHeader}>
              <h3>Postulaciones por Unidad Académica</h3>
            </div>
            <div className={styles.tableroChartRows} style={{ padding: '1.25rem 1.5rem', overflowY: 'auto', flex: 1, minHeight: 0 }}>
              {allUAs.map((ua) => (
                <div key={ua.name} className={styles.tableroChartRow}>
                  <span className={styles.tableroBarLabelWide}>{ua.name}</span>
                  <div className={styles.tableroBarWrap}>
                    <div className={styles.tableroBar} style={{ width: `${Math.round((ua.count / allUAs[0].count) * 100)}%` }} />
                  </div>
                  <span className={styles.tableroBarCount}>{ua.count}</span>
                </div>
              ))}
            </div>
            <div className={styles.exportModalActions}>
              <Button variant="secondary" onClick={() => setShowUAModal(false)}>
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}

      {showExportModal && (
        <div
          className={styles.exportOverlay}
          onClick={() => setShowExportModal(false)}
        >
          <div
            className={styles.exportModal}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.exportModalHeader}>
              <h3>Descargar postulaciones</h3>
              <p>Filtra el contenido antes de descargar. Sin selección se descargan todas.</p>
            </div>

            <div className={styles.exportModalBody}>
              <div className={styles.exportFilters}>
                <MultiSelectSearch
                  label="Estado"
                  options={Object.entries(StatusConfig)
                    .sort((a, b) => a[1].order - b[1].order)
                    .map(([value, cfg]) => ({ value, label: cfg.label }))}
                  selected={exportStatuses}
                  onChange={setExportStatuses}
                />
                {filterOptions.unidadAcademica.length > 0 && (
                  <MultiSelectSearch
                    label="Unidad Académica"
                    options={filterOptions.unidadAcademica.map((u) => ({ value: u, label: u }))}
                    selected={exportAcademicUnits}
                    onChange={setExportAcademicUnits}
                    placeholder="Buscar unidad..."
                  />
                )}
              </div>
              {exportStatuses.length === 0 && exportAcademicUnits.length === 0 && (
                <p className={styles.exportAllHint}>Se descargarán todas las postulaciones sin filtro.</p>
              )}
            </div>

            <div className={styles.exportModalActions}>
              <Button variant="secondary" onClick={() => setShowExportModal(false)}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={() => handleGlobalExport(exportStatuses, exportAcademicUnits)}
              >
                Descargar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
